/**
 * @name: PDP Colorway Swap
 * @description: Swaps the product page section-by-section when a colorway swatch is clicked instead of doing a full page navigation. Keeps the address bar on the real product URL (including ?variant=) so refresh, share and back/forward all behave like a normal navigation.
 * @dependencies: pdp-colorway-swap.css, snippets/colorways.liquid, assets/theme.js
 * @strict-mode: true
 */

(() => {
  'use strict';

  const MAIN_SELECTOR = '#MainContent';
  const SECTION_PREFIX = 'shopify-section-';
  const LINK_SELECTOR = '[data-colorway-link]';
  const SWAP_META_SELECTOR = 'script[data-pdp-swap-meta]';
  /** The Section Rendering API refuses more than five sections per request. */
  const SECTION_CHUNK_SIZE = 5;
  const INERT_SCRIPT_TYPE = 'text/pdp-swap-inert';
  const EXECUTABLE_SCRIPT_TYPES = new Set(['', 'module', 'text/javascript', 'application/javascript']);
  /** Ready events never fire again after first paint, so re-executed scripts get them replayed manually. */
  const READY_EVENTS = new Set(['DOMContentLoaded', 'load']);
  /** Nodes that PDP scripts move out of their own section and therefore outlive a section replacement. */
  const RELOCATED_SELECTOR = '[data-mobile-sticky-cart-bar]';
  const BUSY_CLASS = 'pdp-colorway-swapping';
  /** Only show the busy state if the swap is slow enough to notice, so prefetched swaps stay flicker free. */
  const BUSY_DELAY = 140;
  const PAGE_CACHE_LIMIT = 12;
  const EXTERNAL_SCRIPT_TIMEOUT = 4000;
  const IDLE_PREFETCH_COUNT = 3;
  /** Keep the progress bar visible at least this long, even when the section fetch is fast. */
  const MIN_PROGRESS_MS = 500;
  const PROGRESS_SELECTOR = '.pdp-colorway-progress';
  const HEADER_SELECTOR =
    '#SiteHeader, .site-header, .header-wrapper, header[role="banner"], .shopify-section-header';
  /** Carry the active gallery slide across colorway swaps on mobile and tablet (≤1079px). */
  const GALLERY_QUERY = '(max-width: 1079px)';
  const MOBILE_PRODUCT_SCROLL_QUERY = '(max-width: 1079px)';
  const MOBILE_PRODUCT_SCROLL_OFFSET = 100;
  const GALLERY_SELECTOR = '[data-product-photos]';
  /** Flickity finishes wiring the new gallery slightly after its section scripts run. */
  const GALLERY_RESTORE_ATTEMPTS = 30;

  // Captured before anything can patch them, so nested instrumentation always restores the real API.
  const nativeAddEventListener = EventTarget.prototype.addEventListener;
  const nativeSetInterval = window.setInterval;

  /**
   * @typedef {{ kind: 'sections', markup: Record<string, string> } | { kind: 'document', markup: string }} PageSource
   */

  /** @type {Map<string, Promise<PageSource>>} url -> pending or resolved page source */
  const pageCache = new Map();
  /** @type {Map<string, string>} section id -> untouched server HTML, used to skip sections that did not change */
  const pristineHtml = new Map();
  /** @type {Set<string>} stylesheet hrefs already present in the document */
  const knownStylesheets = new Set();

  let currentPath = window.location.pathname;
  let requestToken = 0;
  /** @type {string[]} section ids of #MainContent, in template order */
  let sectionIds = [];
  /** @type {Array<() => void>} cleanup for listeners and intervals registered by the previous generation of scripts */
  let teardown = [];
  let busyTimer = null;
  let progressHideTimer = null;
  let swapQueue = Promise.resolve();
  /** Slide the incoming gallery should mount on, so it never paints the first slide first. */
  let pendingSlideIndex = 0;
  let scrollProductSectionOnSwap = false;

  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

  const absolute = (url) => new URL(url, window.location.origin).href;

  const isSameOrigin = (url) => {
    try {
      return new URL(url, window.location.origin).origin === window.location.origin;
    } catch (error) {
      return false;
    }
  };

  const sectionsOf = (root) =>
    Array.from(root.children).filter((element) => element.id && element.id.startsWith(SECTION_PREFIX));

  /* Network
     ========================================================================== */

  function trimPageCache() {
    while (pageCache.size > PAGE_CACHE_LIMIT) {
      const oldest = pageCache.keys().next().value;
      pageCache.delete(oldest);
    }
  }

  /**
   * The rendered section carries the request URL in Swym's login `return_to`, which would send the
   * customer back to a JSON response. Drop the parameter we added.
   * @param {string} markup
   */
  const stripSectionsParam = (markup) => markup.replace(/%26sections%3D[^&"']*/g, '');

  /**
   * Renders only #MainContent through the Section Rendering API: roughly a third of the bytes of the
   * full document and, because the chunks run in parallel, noticeably less server time.
   * @param {string} url
   * @returns {Promise<PageSource | null>} null when the target does not expose these exact sections,
   *   which happens when the other colorway uses a different product template.
   */
  async function requestSections(url) {
    if (!sectionIds.length) return null;

    const chunks = [];
    for (let index = 0; index < sectionIds.length; index += SECTION_CHUNK_SIZE) {
      chunks.push(sectionIds.slice(index, index + SECTION_CHUNK_SIZE));
    }

    const responses = await Promise.all(
      chunks.map((chunk) => {
        const target = new URL(url);
        target.searchParams.set('sections', chunk.join(','));
        return fetch(target.href, { credentials: 'same-origin' });
      }),
    );

    if (responses.some((response) => !response.ok)) return null;

    const payloads = await Promise.all(responses.map((response) => response.json()));
    const markup = Object.assign({}, ...payloads);
    if (sectionIds.some((id) => typeof markup[id] !== 'string')) return null;

    for (const id of sectionIds) markup[id] = stripSectionsParam(markup[id]);
    return { kind: 'sections', markup };
  }

  /**
   * @param {string} url
   * @returns {Promise<PageSource>}
   */
  async function requestDocument(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Colorway request failed with ${response.status}`);
    return { kind: 'document', markup: await response.text() };
  }

  /**
   * @param {string} url
   * @returns {Promise<PageSource>}
   */
  function fetchPage(url) {
    const key = absolute(url);
    const cached = pageCache.get(key);
    if (cached) return cached;

    const request = requestSections(key)
      .catch(() => null)
      .then((source) => source || requestDocument(key))
      .catch((error) => {
        pageCache.delete(key);
        throw error;
      });

    pageCache.set(key, request);
    trimPageCache();
    return request;
  }

  function prefetch(url) {
    if (!url || !isSameOrigin(url)) return;
    if (pageCache.has(absolute(url))) return;
    fetchPage(url).catch(() => {});
  }

  /* Script re-execution
     ========================================================================== */

  /**
   * Temporarily records everything the re-executed scripts bind globally so the next swap can undo it,
   * and queues their ready-event callbacks because DOMContentLoaded/load will never fire again.
   */
  function instrument() {
    const documentAdd = nativeAddEventListener.bind(document);
    const windowAdd = nativeAddEventListener.bind(window);
    const pendingReady = [];
    const disposers = [];

    const wrap = (target, add) =>
      function (type, listener, options) {
        if (!listener) return;

        if (READY_EVENTS.has(type)) {
          pendingReady.push({ target, type, listener });
          return;
        }

        add(type, listener, options);
        disposers.push(() => target.removeEventListener(type, listener, options));
      };

    document.addEventListener = wrap(document, documentAdd);
    window.addEventListener = wrap(window, windowAdd);
    window.setInterval = function (...args) {
      const id = nativeSetInterval.apply(window, args);
      disposers.push(() => window.clearInterval(id));
      return id;
    };

    return {
      /** Replays queued ready callbacks once every script in the batch has defined its helpers. */
      flush() {
        while (pendingReady.length) {
          const { target, type, listener } = pendingReady.shift();
          const handler = typeof listener === 'function' ? listener : listener.handleEvent;
          if (typeof handler !== 'function') continue;

          try {
            handler.call(target, new Event(type));
          } catch (_error) {
            // Keep a failed ready callback from aborting the rest of the batch.
          }
        }
      },
      end() {
        delete document.addEventListener;
        delete window.addEventListener;
        window.setInterval = nativeSetInterval;
        return disposers;
      },
    };
  }

  /**
   * Marks scripts inert while they still live in the parsed (inactive) document so insertion can never
   * run them implicitly. They are re-created explicitly in {@link activate}.
   * @param {Element} source
   */
  function neutralizeScripts(source) {
    for (const script of source.querySelectorAll('script')) {
      const type = (script.getAttribute('type') || '').toLowerCase();
      if (!EXECUTABLE_SCRIPT_TYPES.has(type)) continue;

      script.setAttribute('data-pdp-swap-type', type);
      script.setAttribute('type', INERT_SCRIPT_TYPE);
    }
  }

  /**
   * @param {HTMLScriptElement} placeholder
   * @returns {{ element: HTMLScriptElement, loaded: Promise<void> }}
   */
  function reviveScript(placeholder) {
    const element = document.createElement('script');

    for (const attribute of Array.from(placeholder.attributes)) {
      if (attribute.name === 'type' || attribute.name === 'data-pdp-swap-type') continue;
      element.setAttribute(attribute.name, attribute.value);
    }

    const originalType = placeholder.getAttribute('data-pdp-swap-type');
    if (originalType) element.setAttribute('type', originalType);

    let loaded = Promise.resolve();

    if (element.src) {
      // Dynamically inserted scripts default to async; force ordered execution instead.
      element.async = false;
      loaded = new Promise((resolve) => {
        const done = () => resolve();
        element.addEventListener('load', done, { once: true });
        element.addEventListener('error', done, { once: true });
        setTimeout(done, EXTERNAL_SCRIPT_TIMEOUT);
      });
    } else {
      element.textContent = placeholder.textContent;
    }

    return { element, loaded };
  }

  /**
   * Runs every script inside a freshly inserted section and registers its cleanup.
   * @param {Element} node
   */
  async function activate(node) {
    const placeholders = Array.from(node.querySelectorAll(`script[type="${INERT_SCRIPT_TYPE}"]`));
    if (!placeholders.length) return;

    const probe = instrument();
    const pendingLoads = [];

    try {
      for (const placeholder of placeholders) {
        const { element, loaded } = reviveScript(placeholder);
        pendingLoads.push(loaded);
        placeholder.replaceWith(element);
      }

      probe.flush();
      await Promise.all(pendingLoads);
      probe.flush();
    } catch (_error) {
      // A failed script must not block the rest of the section from activating.
    } finally {
      teardown = teardown.concat(probe.end());
    }
  }

  function runTeardown() {
    for (const dispose of teardown) {
      try {
        dispose();
      } catch (_error) {
        // A single disposer failing must not skip the rest of teardown.
      }
    }
    teardown = [];
  }

  /* DOM
     ========================================================================== */

  /**
   * Section stylesheets are lifted into the head once so replacing a section never drops its styles.
   * @param {ParentNode} node
   * @param {boolean} [relocate] Move the live node instead of cloning it, which avoids a repaint on first run.
   */
  function hoistStylesheets(node, relocate) {
    for (const link of node.querySelectorAll('link[rel~="stylesheet"]')) {
      const href = link.href;

      if (href && !knownStylesheets.has(href)) {
        knownStylesheets.add(href);
        document.head.appendChild(relocate ? link : link.cloneNode(true));
        continue;
      }

      link.remove();
    }
  }

  function removeRelocatedNodes() {
    for (const node of document.body.querySelectorAll(RELOCATED_SELECTOR)) {
      if (!node.closest(MAIN_SELECTOR)) node.remove();
    }
  }

  /**
   * @param {'load' | 'unload'} kind
   * @param {Element} element
   */
  function notifySection(kind, element) {
    element.dispatchEvent(
      new CustomEvent(`shopify:section:${kind}`, {
        bubbles: true,
        detail: { sectionId: element.id.slice(SECTION_PREFIX.length) },
      }),
    );
  }

  /**
   * Replaces sections one at a time, leaving untouched ones alone and yielding a frame between
   * rewrites so the buy box repaints before the long tail of marketing sections.
   * @param {Element} main
   * @param {Element[]} incoming
   * @param {number} token
   */
  async function applySections(main, incoming, token) {
    const leftovers = new Map(sectionsOf(main).map((element) => [element.id, element]));
    const placed = [];

    for (const source of incoming) {
      if (token !== requestToken) return;

      const previous = leftovers.get(source.id);
      leftovers.delete(source.id);
      const sourceHtml = source.outerHTML;
      let node;

      if (previous && pristineHtml.get(source.id) === sourceHtml) {
        node = previous;
      } else {
        neutralizeScripts(source);
        node = document.importNode(source, true);
        hoistStylesheets(node);
        preselectSlide(node);

        if (previous) {
          notifySection('unload', previous);
          previous.replaceWith(node);
        } else if (placed.length) {
          placed[placed.length - 1].after(node);
        } else {
          main.prepend(node);
        }

        pristineHtml.set(source.id, sourceHtml);
        await activate(node);
        notifySection('load', node);
      }

      const anchor = placed[placed.length - 1];
      if (anchor && anchor.nextElementSibling !== node) anchor.after(node);
      placed.push(node);

      // Let the section that was just rewritten paint before working on the next one.
      if (node !== previous) await nextFrame();
    }

    for (const orphan of leftovers.values()) {
      notifySection('unload', orphan);
      orphan.remove();
    }
  }

  /**
   * Reads the payload that `snippets/pdp-swap-meta.liquid` embeds in the main product section, which
   * is the only view of the target page's <head> a section-only response gives us.
   * @param {Element | undefined} incomingMain
   * @returns {boolean} whether the head could be refreshed from the section
   */
  function applySwapMeta(incomingMain) {
    const script = incomingMain && incomingMain.querySelector(SWAP_META_SELECTOR);
    if (!script) return false;

    let meta;
    try {
      meta = JSON.parse(script.textContent);
    } catch (_error) {
      return false;
    }

    if (meta.title) document.title = meta.title;

    const mirrored = [
      ['link[rel="canonical"]', 'href', meta.canonical],
      ['meta[name="description"]', 'content', meta.description],
      ['meta[property="og:title"]', 'content', meta.ogTitle],
      ['meta[property="og:description"]', 'content', meta.ogDescription],
      ['meta[property="og:url"]', 'content', meta.canonical],
      ['meta[property="og:image"]', 'content', meta.ogImage],
      ['meta[property="og:image:secure_url"]', 'content', meta.ogImage],
      ['meta[name="twitter:title"]', 'content', meta.ogTitle],
      ['meta[name="twitter:description"]', 'content', meta.ogDescription],
    ];

    for (const [selector, attribute, value] of mirrored) {
      if (typeof value !== 'string') continue;
      const target = document.querySelector(selector);
      if (target) target.setAttribute(attribute, value);
    }

    if (meta.product) {
      window._theme = window._theme || {};
      window._theme.productJson = meta.product;

      // Keep Boost recommendation context in sync for SPA colorway swaps
      if (window.boostSDData) {
        window.boostSDData.product = meta.product;
      }
      if (window.meta?.product) {
        window.meta.product.id = meta.product.id;
        window.meta.product.handle = meta.product.handle;
        window.meta.product.type = meta.product.type;
        window.meta.product.vendor = meta.product.vendor;
      }
      if (window.ShopifyAnalytics?.meta?.product) {
        window.ShopifyAnalytics.meta.product.id = meta.product.id;
        window.ShopifyAnalytics.meta.product.handle = meta.product.handle;
        window.ShopifyAnalytics.meta.product.type = meta.product.type;
        window.ShopifyAnalytics.meta.product.vendor = meta.product.vendor;
      }
    }

    if (meta.reviewFormProductCard) {
      const dataEl = document.getElementById('JudgemeReviewFormProductData');
      if (dataEl) {
        dataEl.textContent = JSON.stringify(meta.reviewFormProductCard);
      }
    }

    return true;
  }

  /**
   * @param {Document} doc
   */
  function updateHead(doc) {
    const title = doc.querySelector('title');
    if (title) document.title = title.textContent;

    const mirrored = [
      ['link[rel="canonical"]', 'href'],
      ['meta[name="description"]', 'content'],
      ['meta[property="og:title"]', 'content'],
      ['meta[property="og:description"]', 'content'],
      ['meta[property="og:url"]', 'content'],
      ['meta[property="og:image"]', 'content'],
    ];

    for (const [selector, attribute] of mirrored) {
      const source = doc.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.setAttribute(attribute, source.getAttribute(attribute) || '');
    }

    for (const script of doc.querySelectorAll('head script:not([src])')) {
      if (!script.textContent.includes('_theme.productJson')) continue;

      try {
        new Function(script.textContent)();
      } catch (_error) {
        // Head product JSON is optional for the swap; keep going without it.
      }
      break;
    }
  }

  /**
   * Judge.me's loader runs once per page. After a section swap the review widget and badges are new
   * DOM nodes, and the SDK's cached jQuery collections still point at the detached ones. This store's
   * Judge.me build exposes `initializeWidgets` (not `loadWidgets`). That call also needs `widget/media.js`
   * for `setupMediaGallery`, which the loader only fetched on the first paint.
   *
   * The AI store summary widget (`store_summary_widget.js`) is a separate ES module with a one-shot
   * manager (`initialized = true` after first run). `initializeWidgets()` does not remount it, so
   * we re-import the module after each swap.
   */
  const STORE_SUMMARY_SELECTOR = '.jdgm-store-summary-widget, [data-widget="store-summary"]';

  function getStoreSummaryScriptUrl() {
    const existing = document.querySelector('script[src*="store_summary_widget.js"]');

    if (existing?.src) {
      return existing.src.split('?')[0];
    }

    const jdgm = window.jdgm;
    const cdn = jdgm?.CDN_HOST || 'https://cdnwidget.judge.me/';
    return `${cdn}widget/store_summary_widget.js`;
  }

  async function reinitStoreSummaryWidgets() {
    if (!document.querySelector(STORE_SUMMARY_SELECTOR)) return;

    const scriptUrl = getStoreSummaryScriptUrl();
    if (!scriptUrl) return;

    const cacheBustUrl = `${scriptUrl}?pdpSwap=${Date.now()}`;

    try {
      await import(/* @vite-ignore */ cacheBustUrl);
      return;
    } catch (_importError) {
      // Some browsers block dynamic import of cross-origin modules; fall back to a module script tag.
    }

    await new Promise((resolve) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = cacheBustUrl;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
      window.setTimeout(resolve, 2500);
    });
  }

  function ensureJudgeMeScript(url, isReady) {
    const jdgm = window.jdgm;
    return new Promise((resolve) => {
      if (!jdgm || typeof jdgm.loadScript !== 'function' || isReady()) {
        resolve();
        return;
      }

      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      jdgm.loadScript(url, done, true);
      window.setTimeout(done, 2500);
    });
  }

  function stubJudgeMeMethod(name) {
    const jdgm = window.jdgm;
    if (jdgm && typeof jdgm[name] !== 'function') {
      jdgm[name] = function () {};
    }
  }

  function refreshJudgeMeCaches() {
    const jdgm = window.jdgm;
    if (!jdgm || typeof jdgm.$ !== 'function' || !jdgm.caches) return;

    jdgm.caches.$widgetWithReviews = jdgm.$('.jdgm-review-widget, .jdgm-revs-tab, .jdgm-all-reviews-widget');
    jdgm.caches.$revWidgets = jdgm.$('.jdgm-review-widget, [data-widget="review"]');
    jdgm.caches.$prevBadges = jdgm.$('.jdgm-preview-badge');
  }

  function initializeJudgeMeWidgets(jdgm) {
    if (typeof jdgm.initializeWidgets === 'function') {
      jdgm.initializeWidgets();
    } else if (typeof jdgm.initWidgets === 'function') {
      jdgm.initWidgets();
    } else if (typeof jdgm.loadWidgets === 'function') {
      jdgm.loadWidgets();
    } else if (typeof jdgm.init === 'function') {
      jdgm.init();
    }
  }

  async function reinitJudgeMe() {
    const jdgm = window.jdgm;
    if (!jdgm) return;

    try {
      const cdn = jdgm.CDN_HOST || 'https://cdnwidget.judge.me/';

      await ensureJudgeMeScript(`${cdn}widget/media.js`, () => typeof jdgm.setupMediaGallery === 'function');
      await ensureJudgeMeScript(`${cdn}widget/form.js`, () => typeof jdgm._renderAndSetupReviewForm === 'function');

      stubJudgeMeMethod('setupMediaGallery');
      stubJudgeMeMethod('_renderAndSetupReviewForm');
      stubJudgeMeMethod('_setupQuestionsForm');
      stubJudgeMeMethod('_setupFormsSubmit');

      refreshJudgeMeCaches();

      const doneWidget = jdgm.DONE_SETUP_WIDGET_CLASS || 'jdgm--done-setup-widget';
      const doneItem = jdgm.DONE_SETUP_CLASS || 'jdgm--done-setup';

      for (const widget of document.querySelectorAll(
        '.jdgm-widget, .jdgm-review-widget, .jdgm-preview-badge, .jdgm-store-summary-widget',
      )) {
        widget.classList.remove(doneWidget, doneItem);
      }

      for (const review of document.querySelectorAll('.jdgm-rev')) {
        review.classList.remove(doneItem);
      }

      initializeJudgeMeWidgets(jdgm);
      await reinitStoreSummaryWidgets();

      if (typeof jdgm.customizeBadges === 'function') {
        jdgm.customizeBadges();
      }

      if (typeof jdgm.triggerVanillaEvent === 'function') {
        jdgm.triggerVanillaEvent('doneLoading');
      } else {
        document.dispatchEvent(new Event('jdgm.doneLoading'));
      }
    } catch (_error) {
      // Judge.me can throw on a partial SDK; reviews stay as server HTML.
    }
  }

  /* Gallery slide
     ========================================================================== */

  /**
   * The mobile and desktop galleries are separate nodes, so pick whichever one is on screen.
   * @returns {HTMLElement | null}
   */
  function visibleGallery() {
    for (const gallery of document.querySelectorAll(GALLERY_SELECTOR)) {
      if (gallery instanceof HTMLElement && gallery.offsetParent !== null) return gallery;
    }
    return null;
  }

  /**
   * @returns {number} the slide the shopper is looking at, or 0 when it should not be carried over
   */
  function captureSlideIndex() {
    if (!window.matchMedia(GALLERY_QUERY).matches) return 0;

    const gallery = visibleGallery();
    if (!gallery) return 0;

    const instance = window.Flickity && window.Flickity.data(gallery);
    if (instance && typeof instance.selectedIndex === 'number') return instance.selectedIndex;

    const selected = gallery.querySelector('.is-selected');
    return selected instanceof HTMLElement ? Number(selected.dataset.index) || 0 : 0;
  }

  /**
   * The theme paints `.product-main-slide:first-child` until Flickity mounts, so the incoming markup
   * is pointed at the carried-over slide before it is inserted. Without this the first photo is
   * visible for the gap between insertion and init.
   * @param {Element} node
   */
  function preselectSlide(node) {
    if (!pendingSlideIndex || !window.matchMedia(GALLERY_QUERY).matches) return;

    for (const gallery of node.querySelectorAll(GALLERY_SELECTOR)) {
      const slides = gallery.querySelectorAll('.product-main-slide');
      const target = slides[pendingSlideIndex];
      if (!target) continue;

      gallery.classList.add('pdp-swap-preserve');
      target.classList.add('pdp-swap-initial');

      // The theme reads the starting slide to decide where the gallery opens.
      for (const slide of slides) {
        slide.classList.toggle('starting-slide', slide === target);
        slide.classList.toggle('secondary-slide', slide !== target);
      }

      const image = target.querySelector('img');
      if (image) {
        image.setAttribute('loading', 'eager');
        image.setAttribute('fetchpriority', 'high');
      }
    }
  }

  /**
   * Flickity paints its starting slide on init, so the carried-over slide has to be handed over as
   * `initialIndex` rather than selected afterwards, which is what caused a visible jump.
   * @param {Element} element
   * @param {object} [options]
   * @returns {object | undefined}
   */
  function withInitialIndex(element, options) {
    if (!pendingSlideIndex || !(element instanceof HTMLElement)) return options;
    if (!element.matches(GALLERY_SELECTOR) || !window.matchMedia(GALLERY_QUERY).matches) return options;

    // Slides are still direct children at this point; Flickity has not wrapped them yet.
    const available = element.children.length;
    const index = pendingSlideIndex;
    pendingSlideIndex = 0;

    if (index >= available) return options;

    return Object.assign({}, options, { initialIndex: index });
  }

  /**
   * @param {Function} Original
   * @returns {Function}
   */
  function wrapFlickity(Original) {
    if (typeof Original !== 'function' || Original.pdpSwapWrapped) return Original;

    class Wrapped extends Original {
      constructor(element, options) {
        super(element, withInitialIndex(element, options));
      }
    }

    // Inherit statics such as `Flickity.data`, which both this file and the theme rely on.
    Object.setPrototypeOf(Wrapped, Original);
    Wrapped.pdpSwapWrapped = true;
    return Wrapped;
  }

  /**
   * Flickity is loaded by a deferred vendor bundle, so hook the assignment rather than the value.
   */
  function installFlickityHook() {
    let wrapped = wrapFlickity(window.Flickity);

    Object.defineProperty(window, 'Flickity', {
      configurable: true,
      get: () => wrapped,
      set: (value) => {
        wrapped = wrapFlickity(value);
      },
    });
  }

  /**
   * Colorways rarely share a photo count, so a slide the new product does not have falls back to the first.
   * @param {number} index
   */
  function restoreSlideIndex(index) {
    if (index <= 0 || !window.matchMedia(GALLERY_QUERY).matches) return;

    let attempts = 0;

    const attempt = () => {
      if (++attempts > GALLERY_RESTORE_ATTEMPTS) return;

      const gallery = visibleGallery();
      const instance = gallery && window.Flickity && window.Flickity.data(gallery);

      if (!instance || !instance.cells || !instance.cells.length) {
        window.requestAnimationFrame(attempt);
        return;
      }

      if (index < instance.cells.length && instance.selectedIndex !== index) instance.select(index, false, true);
    };

    window.requestAnimationFrame(attempt);
  }

  function scrollToProductSection() {
    if (!window.matchMedia(MOBILE_PRODUCT_SCROLL_QUERY).matches) return;

    const section = document.querySelector('[data-section-type="product"]')?.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) return;

    const top = section.getBoundingClientRect().top + window.scrollY - MOBILE_PRODUCT_SCROLL_OFFSET;

    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }

  function afterSwap(url) {
    if (window.AOS && typeof window.AOS.refreshHard === 'function') window.AOS.refreshHard();
    reinitJudgeMe().catch(() => {});

    // Title wishlist is rebuilt via SwymWishlistPDPButtonAPI on pdp:swapped (swym-custom.liquid).
    document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
    document.dispatchEvent(
      new CustomEvent('pdp:swapped', {
        detail: {
          url: absolute(url),
          product: window._theme?.productJson || null,
        },
      }),
    );
    window.dispatchEvent(new Event('resize'));

    if (scrollProductSectionOnSwap) {
      scrollProductSectionOnSwap = false;
      scrollToProductSection();
    }
  }

  /* Busy state
     ========================================================================== */

  /**
   * Thin top progress line (Danetti-style). Lives outside #MainContent so section
   * swaps never remove it. Does not change fetch/replace behaviour.
   * @returns {HTMLElement}
   */
  function ensureProgressBar() {
    let bar = document.querySelector(PROGRESS_SELECTOR);
    if (bar instanceof HTMLElement) return bar;

    bar = document.createElement('div');
    bar.className = 'pdp-colorway-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    return bar;
  }

  /**
   * Pin the bar just under the sticky/site header.
   * @param {HTMLElement} bar
   */
  function positionProgressBar(bar) {
    const header = document.querySelector(HEADER_SELECTOR);
    const top = header instanceof HTMLElement ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    bar.style.top = `${top}px`;
  }

  function startProgress() {
    window.clearTimeout(progressHideTimer);
    progressHideTimer = null;

    const bar = ensureProgressBar();
    positionProgressBar(bar);
    bar.classList.remove('is-active');
    // Restart the scale animation from 0 even on rapid successive swaps.
    void bar.offsetWidth;
    bar.classList.add('is-active');
  }

  function stopProgress() {
    const bar = document.querySelector(PROGRESS_SELECTOR);
    if (!(bar instanceof HTMLElement)) return;

    window.clearTimeout(progressHideTimer);
    progressHideTimer = null;
    // Drop active immediately so the bar never lingers after sections are on screen.
    bar.classList.remove('is-active');
  }

  function startBusy() {
    startProgress();
    if (busyTimer) return;
    busyTimer = window.setTimeout(() => document.documentElement.classList.add(BUSY_CLASS), BUSY_DELAY);
  }

  function stopBusy() {
    window.clearTimeout(busyTimer);
    busyTimer = null;
    document.documentElement.classList.remove(BUSY_CLASS);
    stopProgress();
  }

  /* Swap
     ========================================================================== */

  /**
   * Rapid clicks queue behind each other; superseded swaps bail out on the token check
   * rather than interleaving DOM writes.
   * @param {string} url
   * @param {{ push?: boolean }} [options]
   */
  function swapTo(url, options = {}) {
    const token = ++requestToken;
    startBusy();

    swapQueue = swapQueue
      .then(() => performSwap(url, token, options.push !== false))
      .catch(() => {});

    return swapQueue;
  }

  /**
   * @param {string} url
   * @param {number} token
   * @param {boolean} push
   */
  async function performSwap(url, token, push) {
    if (token !== requestToken) return;

    const swapStartedAt = Date.now();
    const slideIndex = captureSlideIndex();
    pendingSlideIndex = slideIndex;

    try {
      let source;
      try {
        source = await fetchPage(url);
      } catch (_error) {
        window.location.assign(url);
        return;
      }

      if (token !== requestToken) return;

      // Hold before section replace so the progress bar is visible even on cached/fast responses.
      const waitMs = MIN_PROGRESS_MS - (Date.now() - swapStartedAt);
      if (waitMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, waitMs));
        if (token !== requestToken) return;
      }

      // Both shapes are parsed into an inactive document so scripts stay dormant and the resulting
      // markup is normalised the same way, which keeps the unchanged-section comparison honest.
      const combined =
        source.kind === 'sections' ? sectionIds.map((id) => source.markup[id]).join('') : source.markup;
      const doc = new DOMParser().parseFromString(combined, 'text/html');
      const incoming = sectionsOf(source.kind === 'sections' ? doc.body : doc.querySelector(MAIN_SELECTOR) || doc.body);
      const main = document.querySelector(MAIN_SELECTOR);

      if (!incoming.length || !main) {
        window.location.assign(url);
        return;
      }

      runTeardown();
      removeRelocatedNodes();
      if (!applySwapMeta(incoming[0]) && source.kind === 'document') updateHead(doc);

      if (push) window.history.pushState({ colorwaySwap: true }, '', absolute(url));
      currentPath = new URL(absolute(url)).pathname;

      await applySections(main, incoming, token);
      if (token !== requestToken) return;

      afterSwap(url);
      restoreSlideIndex(slideIndex);
    } finally {
      pendingSlideIndex = 0;
      // Always clear the bar once this swap is done (or failed), unless a newer swap owns it.
      if (token === requestToken) {
        stopBusy();
      }
    }
  }

  /* Wiring
     ========================================================================== */

  /**
   * @param {Event} event
   * @returns {HTMLAnchorElement | null}
   */
  const linkFrom = (event) => (event.target instanceof Element ? event.target.closest(LINK_SELECTOR) : null);

  function onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = linkFrom(event);
    if (!link || link.target === '_blank' || !isSameOrigin(link.href)) return;

    event.preventDefault();
    link.classList.add('is-selecting');
    scrollProductSectionOnSwap = true;
    scrollToProductSection();
    swapTo(link.href);
  }

  function onIntent(event) {
    const link = linkFrom(event);
    if (link) prefetch(link.href);
  }

  /**
   * Hovering a swatch is a deliberate signal, but speculative idle prefetching is a bandwidth gamble
   * that is not worth taking on a metered or slow connection.
   */
  function idlePrefetchAllowed() {
    const connection = navigator.connection;
    if (!connection) return true;
    if (connection.saveData) return false;

    return !String(connection.effectiveType || '').includes('2g');
  }

  function prefetchNearby() {
    if (!idlePrefetchAllowed()) return;

    const links = Array.from(document.querySelectorAll(LINK_SELECTOR)).slice(0, IDLE_PREFETCH_COUNT);
    for (const link of links) prefetch(link.href);
  }

  function scheduleIdlePrefetch() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(prefetchNearby, { timeout: 3000 });
      return;
    }
    window.setTimeout(prefetchNearby, 1500);
  }

  function onPopState() {
    const target = window.location.href;
    if (new URL(target).pathname === currentPath) return;

    swapTo(target, { push: false });
  }

  function init() {
    const main = document.querySelector(MAIN_SELECTOR);
    if (!main) return;

    sectionIds = sectionsOf(main).map((section) => section.id.slice(SECTION_PREFIX.length));

    installFlickityHook();

    // Snapshot before any script mutates the DOM so unchanged sections can be detected later.
    for (const section of sectionsOf(main)) pristineHtml.set(section.id, section.outerHTML);
    for (const link of document.head.querySelectorAll('link[rel~="stylesheet"]')) knownStylesheets.add(link.href);
    for (const section of sectionsOf(main)) hoistStylesheets(section, true);

    document.addEventListener('click', onClick);
    document.addEventListener('pointerover', onIntent, { passive: true });
    document.addEventListener('touchstart', onIntent, { passive: true });
    document.addEventListener('focusin', onIntent);
    document.addEventListener('colorways:rendered', scheduleIdlePrefetch);
    window.addEventListener('popstate', onPopState);

    scheduleIdlePrefetch();
  }

  init();
})();
